import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, JSX, RefObject } from 'react'
import { createPortal } from 'react-dom'

/**
 * ══ LỊCH BUNG RA CỦA Ô NGÀY ══════════════════════════════════════════════════════════════════
 *
 * ⭐ DỰNG 18/09/2026. `DateInput` trước đó CỐ Ý chưa chép lịch của bản cũ (`ui.tsx:490`) với lý do
 *   ghi ngay trong mã: *"dựng lịch nửa vời (bị modal cắt, bấm ra ngoài không đóng) tệ hơn là chưa
 *   có"*. Đợt soi 17–18/09 đo lại thì đây là lệch gặp ở MỌI màn có ô ngày — khoá sổ, thư đòi nợ,
 *   giá cước, hồ sơ hợp đồng, dashboard — nên nó đáng dựng, và dựng cho ĐÚNG hai điều kiện đó:
 *
 * ⚠ PORTAL + `position: fixed`. Hầu hết ô ngày nằm TRONG `<Modal>`, mà modal có `overflow-y-auto`:
 *   một popup `absolute` bên trong sẽ bị CẮT ở mép modal — đúng lỗi bản cũ từng dính và phải vá
 *   bằng portal. Toạ độ chốt theo ô nhập lúc mở, tự lật LÊN TRÊN khi sát đáy màn hình.
 * ⚠ BẤM RA NGOÀI PHẢI ĐÓNG, và "ngoài" gồm cả chính ô nhập: popup nằm ở `document.body` nên
 *   `contains` của thẻ bọc không thấy nó — phải soi CẢ HAI ref.
 * ⚠ Esc đóng LỊCH chứ không đóng modal đang chứa nó: chặn nổi bọt, nếu không người dùng mất cả
 *   phiếu đang gõ chỉ vì muốn tắt cái lịch.
 */

const WEEKDAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'] as const
const POPUP_H = 300
const POPUP_W = 248

const pad = (n: number): string => String(n).padStart(2, '0')
const isoOf = (y: number, m: number, d: number): string => `${String(y)}-${pad(m + 1)}-${pad(d)}`

interface DayCell { d: number; y: number; m: number; inMonth: boolean }

export const CalendarPopup = (
  { valueIso, anchorRect, popRef, onPick, onClose, todayLabel }: {
    valueIso: string
    anchorRect: DOMRect | null
    popRef: RefObject<HTMLDivElement | null>
    onPick: (iso: string) => void
    onClose: () => void
    todayLabel: string
  },
): JSX.Element => {
  const sel = /^\d{4}-\d{2}-\d{2}$/.test(valueIso) ? new Date(`${valueIso}T00:00:00`) : null
  const base = sel ?? new Date()
  const [view, setView] = useState({ y: base.getFullYear(), m: base.getMonth() })

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => { window.removeEventListener('keydown', onKey, true) }
  }, [onClose])

  const firstDay = new Date(view.y, view.m, 1)
  // Tuần bắt đầu THỨ HAI (lịch VN), không phải Chủ nhật như mặc định của `getDay()`.
  const startOffset = (firstDay.getDay() + 6) % 7
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate()
  const prevDays = new Date(view.y, view.m, 0).getDate()

  const cells: DayCell[] = []
  for (let i = startOffset - 1; i >= 0; i -= 1) {
    const pm = view.m - 1
    cells.push({ d: prevDays - i, y: pm < 0 ? view.y - 1 : view.y, m: (pm + 12) % 12, inMonth: false })
  }
  for (let d = 1; d <= daysInMonth; d += 1) cells.push({ d, y: view.y, m: view.m, inMonth: true })
  let next = 1
  while (cells.length % 7 !== 0) {
    const nm = view.m + 1
    cells.push({ d: next, y: nm > 11 ? view.y + 1 : view.y, m: nm % 12, inMonth: false })
    next += 1
  }

  const shift = (delta: number): void => {
    const m = view.m + delta
    setView({ y: view.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 })
  }

  const today = new Date()
  const same = (date: Date | null, c: DayCell): boolean =>
    date !== null && date.getFullYear() === c.y && date.getMonth() === c.m && date.getDate() === c.d

  let style: CSSProperties = { position: 'absolute', zIndex: 50, marginTop: 4 }
  if (anchorRect !== null) {
    const top = anchorRect.bottom + 4 + POPUP_H > window.innerHeight
      ? Math.max(8, window.innerHeight - POPUP_H - 8)
      : anchorRect.bottom + 4
    const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - POPUP_W - 8))
    style = { position: 'fixed', top, left, zIndex: 9999 }
  }

  const navCls = 'grid h-6 w-6 place-items-center rounded text-slate-400 hover:bg-slate-100 hover:text-brand-600'

  return createPortal(
    <div ref={popRef} style={style} role="dialog" aria-label={`${pad(view.m + 1)}/${String(view.y)}`}
         className="w-[248px] rounded-xl border border-slate-200 bg-white p-2.5 text-slate-700 shadow-lg">
      <div className="mb-1.5 flex items-center justify-between">
        <button type="button" aria-label="‹" className={navCls} onClick={() => { shift(-1) }}>‹</button>
        <span className="text-[12px] font-bold">{pad(view.m + 1)}/{view.y}</span>
        <button type="button" aria-label="›" className={navCls} onClick={() => { shift(1) }}>›</button>
      </div>
      <div className="mb-1 grid grid-cols-7 gap-0.5 text-center text-[10px] font-bold text-slate-400">
        {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((c) => {
          const iso = isoOf(c.y, c.m, c.d)
          const picked = same(sel, c)
          const isToday = same(today, c)
          return (
            <button
              key={iso} type="button" aria-label={iso}
              onClick={() => { onPick(iso) }}
              className={`h-7 rounded text-[11px] ${picked
                ? 'bg-brand-500 font-bold text-white'
                : isToday ? 'bg-brand-50 font-bold text-brand-700' : c.inMonth ? 'hover:bg-slate-100' : 'text-slate-300 hover:bg-slate-50'}`}>
              {c.d}
            </button>
          )
        })}
      </div>
      <button type="button"
              onClick={() => { onPick(isoOf(today.getFullYear(), today.getMonth(), today.getDate())) }}
              className="mt-1.5 w-full rounded py-1 text-[11px] font-semibold text-brand-600 hover:bg-brand-50">
        {todayLabel}
      </button>
    </div>,
    document.body,
  )
}

/** Chỉ để `DateInput` dùng lại — giữ ref của popup để phép "bấm ra ngoài" soi được cả nó. */
export const usePopupRef = (): RefObject<HTMLDivElement | null> => useRef<HTMLDivElement>(null)
